import type { QueryClient } from '@tanstack/react-query';
import type {
  LayoutsActionsLinksResponse,
  LayoutsLayoutResponse,
  LayoutsLayoutsResponse,
  LayoutsNewslettersLinksResponse,
  LayoutsStarterLayoutsResponse,
  LayoutsTransactionalLinksResponse,
} from '@customerio/hydra-ui-api';
import type {
  ServicesApiGetLayoutNewslettersResponse,
  ServicesApiGetLayoutTransactionalMessagesResponse,
} from '@/services/api/services-api';
import { actionKeys, parseAction, type Action } from '../action/entity';
import { campaignKeys, parseCampaign, type Campaign } from '../campaign/entity';
import { layoutKeys, parseLayout, parseLayoutStarter, type Layout, type LayoutStarter } from './entity';

export interface ParsedLayoutsListResponse {
  layouts: readonly Layout[];
  meta?: LayoutsLayoutsResponse['meta'];
}

export function parseLayoutsListResponse(raw: LayoutsLayoutsResponse): ParsedLayoutsListResponse {
  return {
    layouts: raw.layouts.map(parseLayout),
    meta: raw.meta,
  };
}

export function populateLayoutsList(qc: QueryClient, workspaceId: number, parsed: ParsedLayoutsListResponse): void {
  for (const l of parsed.layouts) qc.setQueryData(layoutKeys.detail(workspaceId, l.id), l);
}

export function parseLayoutDetailResponse(raw: LayoutsLayoutResponse): Layout {
  if (raw.layout == null) {
    throw new Error('missing layout in get layout response');
  }
  return parseLayout(raw.layout);
}

export function populateLayoutDetail(qc: QueryClient, workspaceId: number, parsed: Layout): void {
  qc.setQueryData(layoutKeys.detail(workspaceId, parsed.id), parsed);
}

export function parseLayoutMutationResponse(raw: LayoutsLayoutsResponse): Layout {
  if (raw.layouts.length !== 1) {
    throw new Error(`Layout mutation response included ${raw.layouts.length} layouts; expected exactly one`);
  }

  const rawLayout = raw.layouts[0]!;
  return parseLayout(rawLayout);
}

export function parseLayoutStartersResponse(raw: LayoutsStarterLayoutsResponse): readonly LayoutStarter[] {
  return raw.layouts.map(parseLayoutStarter);
}

export interface ParsedLayoutActionsResponse {
  actions: readonly Action[];
  campaigns: readonly Campaign[];
}

export function parseLayoutActionsResponse(raw: LayoutsActionsLinksResponse): ParsedLayoutActionsResponse {
  return {
    actions: raw.actions.map(parseAction),
    campaigns: raw.campaigns.map((c) => parseCampaign(c as unknown as Parameters<typeof parseCampaign>[0])),
  };
}

export function populateLayoutActions(qc: QueryClient, workspaceId: number, parsed: ParsedLayoutActionsResponse): void {
  for (const a of parsed.actions) qc.setQueryData(actionKeys.detail(workspaceId, a.id), a);
  for (const c of parsed.campaigns) qc.setQueryData(campaignKeys.detail(workspaceId, c.id), c);
}

// Broadcasts, transactional messages, and templates do not have dedicated
// DB entities yet. Keep these usage responses in their Services API shape
// so the follow-up page migration can render the exact joined rows the
// layout usage tables need without implying reusable normalized caches. The
// generated client returns the same wire JSON, so we bridge the type here.
export type ParsedLayoutNewslettersResponse = ServicesApiGetLayoutNewslettersResponse;

export function parseLayoutNewslettersResponse(raw: LayoutsNewslettersLinksResponse): ParsedLayoutNewslettersResponse {
  return raw as unknown as ParsedLayoutNewslettersResponse;
}

export type ParsedLayoutTransactionalMessagesResponse = ServicesApiGetLayoutTransactionalMessagesResponse;

export function parseLayoutTransactionalMessagesResponse(
  raw: LayoutsTransactionalLinksResponse,
): ParsedLayoutTransactionalMessagesResponse {
  return raw as unknown as ParsedLayoutTransactionalMessagesResponse;
}
